/**
 * videoPollAdapter - Normalizes the video generation status response
 * into a generic `PollUpdate` consumed by GenerationContext.
 *
 * Self-registers on module load so that GenerationContext can resume
 * polling persisted video jobs after a reload.
 */

import { VideoGenerationAPI } from '../api';
import type { GenerationResponse, GenerationStatusResponse } from '../../types';
import type { PollAdapter, PollUpdate } from '../../types/generationJob';
import { sanitizeBackendError } from '../../utils/errorSanitizer';
import { registerPollAdapter } from './pollAdapterRegistry';

export interface VideoJobResult {
  videoUrl: string | undefined;
  result: GenerationResponse;
}

/**
 * Extracts timing fields that are present in the actual API response but not
 * declared on `GenerationStatusResponse` (they arrive as extra top-level keys).
 */
function extractTiming(data: GenerationStatusResponse): {
  startedAt: string | null;
  elapsedSeconds: number | null;
  estimatedRemainingSeconds: number | null;
  estimatedTotalSeconds: number | null;
  currentStage: string | null;
  currentStageLabel: string | null;
  currentStep: number | null;
  totalSteps: number | null;
} {
  const extra = data as unknown as Record<string, unknown>;
  return {
    startedAt: (extra.started_at as string | null) ?? null,
    elapsedSeconds: (extra.elapsed_seconds as number | null) ?? null,
    estimatedRemainingSeconds: (extra.estimated_remaining_seconds as number | null) ?? null,
    estimatedTotalSeconds: (extra.estimated_total_seconds as number | null) ?? null,
    currentStage: (extra.current_stage as string | null) ?? null,
    currentStageLabel: (extra.current_stage_label as string | null) ?? null,
    currentStep: (extra.current_step as number | null) ?? null,
    totalSteps: (extra.total_steps as number | null) ?? null,
  };
}

function buildGenerationResponse(
  jobId: string,
  data: GenerationStatusResponse
): GenerationResponse {
  // The canonical job-status route (shared/routes/jobs.py) serializes the
  // RunPod worker output under `extra_metadata`, NOT `metadata`.  Other
  // adapters (brollPollAdapter, dubbing, transcription) read `extra_metadata`
  // correctly; this adapter previously read `metadata` and silently lost
  // every nested field (video_url fallback, error_code, file_size, etc.),
  // turning sanitized SERVICE_UNAVAILABLE worker failures into fake-success
  // screens in the UI (no video player, no size, just a green checkmark).
  // Read the canonical field; fall back to `metadata` only for the (now
  // hypothetical) local-mode route that still produces a flat object.
  const extra = data as unknown as Record<string, unknown>;
  const metadata = (extra.extra_metadata ?? data.metadata ?? {}) as Record<string, unknown>;
  return {
    success: true,
    message: 'Video generated successfully!',
    generation_id: jobId,
    video_path: data.video_path ?? (metadata.video_path as string | undefined) ?? 'video.mp4',
    // result_url is the single canonical field on the job-status response.
    // Fall back to extra_metadata.video_url for stores that still nest it there.
    video_url:
      (extra.result_url as string | undefined) ?? (metadata.video_url as string | undefined),
    filename: (data.filename ?? metadata.filename) as string | undefined,
    file_size: (data.file_size ?? metadata.file_size) as number | undefined,
    duration: data.duration ?? (metadata.duration as number | undefined),
    resolution: data.resolution ?? (metadata.resolution as string | undefined),
    stats: data.stats,
    subtitle_url: (extra.subtitle_url ?? metadata.subtitle_url) as string | undefined,
    has_subtitles: (extra.has_subtitles ?? metadata.has_subtitles ?? false) as boolean,
    subtitle_filename: (extra.subtitle_filename ?? metadata.subtitle_filename) as
      | string
      | undefined,
    subtitle_files: (extra.subtitle_files ?? metadata.subtitle_files) as
      | Record<string, string>
      | undefined,
    available_languages: (extra.available_languages ?? metadata.available_languages) as
      | string[]
      | undefined,
    subtitle_url_srt: (extra.subtitle_url_srt ?? metadata.subtitle_url_srt) as string | undefined,
    subtitle_files_srt: (extra.subtitle_files_srt ?? metadata.subtitle_files_srt) as
      | Record<string, string>
      | undefined,
  };
}

/**
 * Detect a sanitized worker-failure envelope smuggled inside a `completed` job
 * record.  When `JobConfiguration(job)` or any early validation crashes, the
 * worker's `StandardErrorHandler.create_error_response` returns
 * `{ok: false, error_code: "SERVICE_UNAVAILABLE", error_id}`
 * and RunPod marks the request as COMPLETED (the handler did exit cleanly).
 * The shared job route preserves this payload under `extra_metadata`, so the
 * frontend MUST inspect it before declaring success — otherwise it renders a
 * fake "Video Generated Successfully" screen (no video, no metadata) and the
 * user is told their job worked when in fact it failed.
 *
 * No refund is claimed from here: the worker does not know the billing state
 * (the refund, if any, happens later on Cloud Run's settle path), so the
 * sanitizer gets only the code and id and shows the no-refund-claim message.
 */
function isSanitizedWorkerFailure(data: GenerationStatusResponse): {
  isFailure: boolean;
  errorCode?: string;
  errorId?: string;
} {
  const extra = data as unknown as Record<string, unknown>;
  const meta = (extra.extra_metadata ?? data.metadata ?? {}) as Record<string, unknown>;
  const ok = meta.ok;
  if (ok === false) {
    return {
      isFailure: true,
      errorCode: (meta.error_code as string | undefined) ?? 'SERVICE_UNAVAILABLE',
      errorId: meta.error_id as string | undefined,
    };
  }
  // Some pre-sanitization worker code paths set error_code without ok=false.
  if (typeof meta.error_code === 'string' && meta.error_code !== 'OK') {
    return {
      isFailure: true,
      errorCode: meta.error_code,
      errorId: meta.error_id as string | undefined,
    };
  }
  return { isFailure: false };
}

export const videoPollAdapter: PollAdapter = async (
  jobId: string
): Promise<PollUpdate<VideoJobResult>> => {
  const response = await VideoGenerationAPI.getGenerationProgress(jobId);

  if (!response.success) {
    // JOB_NOT_FOUND means the backend restarted and its in-memory store is empty.
    // This is not a real failure — it is a stale job ID from a previous session.
    // Return 'expired' with the sentinel code so GenerationContext silently drops
    // the job without showing an error banner or Retry button.
    if (response.errorCode === 'JOB_NOT_FOUND') {
      console.info(`[Generation] Dropping stale job ${jobId} (backend restarted)`);
      return {
        done: true,
        status: 'expired',
        error: { message: 'Stale job from previous backend session', code: 'JOB_NOT_FOUND' },
      };
    }
    return {
      done: true,
      status: 'failed',
      error: {
        message: response.error ?? 'Failed to get video generation status',
        sanitized: sanitizeBackendError({ error_code: response.errorCode }),
      },
    };
  }

  const data = response.data;
  if (!data) {
    return {
      done: true,
      status: 'failed',
      error: {
        message: 'Empty status response from server',
        sanitized: sanitizeBackendError(null),
      },
    };
  }

  const timing = extractTiming(data);
  const pollTiming = {
    startedAt: timing.startedAt,
    elapsedSeconds: timing.elapsedSeconds,
    estimatedRemainingSeconds: timing.estimatedRemainingSeconds,
    estimatedTotalSeconds: timing.estimatedTotalSeconds,
    currentStage: timing.currentStage,
    currentStageLabel: timing.currentStageLabel,
    currentStep: timing.currentStep,
    totalSteps: timing.totalSteps,
  };

  const status = data.status;

  // Terminal: completed
  if (status === 'completed') {
    // RunPod reports COMPLETED whenever the handler returns cleanly, including
    // when it returns a `StandardErrorHandler.create_error_response` envelope
    // (`{ok:false, error_code:"SERVICE_UNAVAILABLE", error_id, ...}`).  We MUST
    // unwrap that here, otherwise the renderer treats every sanitized worker
    // failure as a green-checkmark success with no video and no metadata.
    const failureProbe = isSanitizedWorkerFailure(data);
    if (failureProbe.isFailure) {
      return {
        done: true,
        status: 'failed',
        error: {
          message: data.error ?? data.message ?? 'Video generation failed',
          code: failureProbe.errorCode,
          sanitized: sanitizeBackendError({
            error_code: failureProbe.errorCode,
            error_id: failureProbe.errorId,
          }),
        },
        timing: pollTiming,
      };
    }
    const generationResponse = buildGenerationResponse(jobId, data);
    return {
      done: true,
      status: 'completed',
      progress: 100,
      stage: 'completed',
      result: { videoUrl: generationResponse.video_url, result: generationResponse },
      timing: pollTiming,
    };
  }

  // Terminal: failed / error
  if (status === 'failed' || status === 'error') {
    return {
      done: true,
      status: 'failed',
      error: {
        message: data.error ?? data.message ?? 'Video generation failed',
        sanitized: sanitizeBackendError(data),
      },
      timing: pollTiming,
    };
  }

  // Non-terminal: queued / processing / running / any other status
  const metadata = data.metadata ?? {};
  return {
    done: false,
    status: 'running',
    progress:
      typeof data.progress === 'number'
        ? data.progress
        : typeof metadata.progress === 'number'
          ? (metadata.progress as number)
          : undefined,
    stage: status,
    message: data.message ?? (metadata.message as string | undefined),
    timing: pollTiming,
  };
};

registerPollAdapter('video', videoPollAdapter);
